/**
 * Helpers de datos para Cotizaciones (admin): formato, estatus, búsqueda,
 * formulario de edición <-> payload para el backend, y el texto para WhatsApp.
 */

export const formatCurrency = (amount) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(amount || 0);

export const STATUS_LABELS = { nueva: "Nueva", enviada: "Enviada", aceptada: "Aceptada", perdida: "Perdida" };

export const STATUS_COLORS = {
  nueva: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  enviada: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  aceptada: "bg-green-500/20 text-green-300 border-green-500/30",
  perdida: "bg-gray-500/20 text-gray-300 border-gray-500/30",
};

// Compatibilidad: cotizaciones viejas (esquema anterior) usaban "cliente" en vez de "nombre".
export const displayName = (quote) => quote.nombre || quote.cliente || "Sin nombre";
export const displayPhone = (quote) => quote.telefono || "";

export const serviceSummary = (quote) => {
  const s = quote.servicios || {};
  const parts = [];
  if (s.cabina?.selected) parts.push(s.cabina.label || "Cabina de Fotos");
  if (s.video360?.selected) parts.push(s.video360.label || "Video 360°");
  if (!parts.length && quote.servicio_principal) parts.push(quote.servicio_principal);
  return parts.join(" + ") || "—";
};

export const filterQuotes = (quotes, term) => {
  const q = (term || "").trim().toLowerCase();
  if (!q) return quotes;
  return quotes.filter((qq) => {
    const folio = (qq.folio || "").toLowerCase();
    const name = displayName(qq).toLowerCase();
    const phone = displayPhone(qq).toLowerCase();
    return folio.includes(q) || name.includes(q) || phone.includes(q);
  });
};

export const sortByCreatedDesc = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);

export const emptyQuoteEditForm = (quote) => ({
  nombre: displayName(quote),
  telefono: quote.telefono || "",
  fecha_evento: quote.fecha_evento || "",
  salon: quote.salon || "",
  zona: quote.zona || "local",
  factura: !!quote.factura,
  cabina_selected: !!quote.servicios?.cabina?.selected,
  cabina_hours: quote.servicios?.cabina?.hours || 2,
  cabina_keychain: !!quote.servicios?.cabina?.keychain,
  cabina_label: quote.servicios?.cabina?.label || "",
  cabina_precio: quote.servicios?.cabina?.precio || 0,
  video360_selected: !!quote.servicios?.video360?.selected,
  video360_nieve: !!quote.servicios?.video360?.nieve,
  video360_label: quote.servicios?.video360?.label || "",
  video360_precio: quote.servicios?.video360?.precio || 0,
  subtotal: quote.subtotal || 0,
  total: quote.total ?? 0,
  status: quote.status || "nueva",
});

export const quoteFormToPayload = (form) => ({
  nombre: form.nombre,
  telefono: form.telefono,
  fecha_evento: form.fecha_evento,
  salon: form.salon,
  zona: form.zona,
  factura: form.factura,
  servicios: {
    cabina: form.cabina_selected
      ? { selected: true, hours: parseInt(form.cabina_hours) || 0, keychain: form.cabina_keychain, label: form.cabina_label, precio: parseFloat(form.cabina_precio) || 0 }
      : { selected: false },
    video360: form.video360_selected
      ? { selected: true, nieve: form.video360_nieve, label: form.video360_label, precio: parseFloat(form.video360_precio) || 0 }
      : { selected: false },
  },
  subtotal: parseFloat(form.subtotal) || 0,
  total: parseFloat(form.total) || 0,
  status: form.status,
});

export const buildQuoteWhatsAppText = (quote, pdfTextos) => {
  const s = quote.servicios || {};
  const lines = [`*Cotización PicParty* - Folio ${quote.folio}`, ""];
  if (s.cabina?.selected) lines.push(`${s.cabina.label || "Cabina de Fotos"}: ${formatCurrency(s.cabina.precio)}`);
  if (s.video360?.selected) lines.push(`${s.video360.label || "Video 360°"}: ${formatCurrency(s.video360.precio)}`);
  lines.push("", `*TOTAL NETO: ${formatCurrency(quote.total)}*`);
  lines.push(quote.factura ? "Con factura (el total no cambia, el precio ya incluye IVA)" : "Sin factura");
  if (quote.salon) lines.push(`Lugar/Salón: ${quote.salon}`);
  lines.push(quote.zona === "foraneo" ? "Zona: fuera de Chihuahua capital (flete a confirmar)" : "Zona: Chihuahua capital");
  lines.push("", pdfTextos?.nota_precios || "");
  lines.push([pdfTextos?.nota_vigencia, pdfTextos?.nota_no_contrato].filter(Boolean).join(" "));
  lines.push(`Aviso de privacidad: ${pdfTextos?.privacy_url || ""}`);
  return lines.join("\n");
};
